import { styled } from "styled-components";

import { RightArrow, SectionTitleUnderlined, UIButton } from "common/components";
import { useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { DimensionItem } from "domains/createMapDetail/components/keyIssueList/DimensionItem";
import { DimensionLimitWarning } from "domains/createMapDetail/components/keyIssueList/DimensionLimitWarning";
import {
  getExistingDimensionIds,
  getLocalImage,
  getMapIsPending,
  getNewCustomDimensions,
} from "domains/createMapDetail/selectors";
import { useDimensions } from "domains/dimension/useDimensions";

const StyledContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const StyledArrowIcon = styled(RightArrow)`
  height: 14px;
  width: 14px;
  margin-left: 12px;
`;

type KeyIssuesSectionProps = { onAddRemoveSelect: () => void; onEditKeyIssueSelect: (id: string) => void };

export const KeyIssuesSection = ({ onAddRemoveSelect, onEditKeyIssueSelect }: KeyIssuesSectionProps) => {
  const translate = useTranslate();
  const isPending = useSelector(getMapIsPending);
  const localImage = useSelector(getLocalImage);
  const existingDimensionIds = useSelector(getExistingDimensionIds);
  const newDimensions = useSelector(getNewCustomDimensions);

  const existingDimensions = useDimensions(existingDimensionIds);
  const dimensions = [...existingDimensions.map(({ dimension }) => dimension), ...newDimensions];

  const seeAllButton = (
    <UIButton variant="neutral" onClick={onAddRemoveSelect} data-test-id="add-remove-key-issue-button">
      {translate("app.create.key_issues.add_remove")}
      <StyledArrowIcon />
    </UIButton>
  );

  return dimensions.length === 0 && isPending ? null : (
    <StyledContainer>
      <SectionTitleUnderlined rightElement={seeAllButton}>{translate("app.topic.key_issues")}</SectionTitleUnderlined>
      <DimensionLimitWarning />
      {dimensions.length === 0 ? (
        <UIButton
          data-test-id="add-remove-key-issue-button"
          fullwidth
          onClick={onAddRemoveSelect}
          style={{ marginBottom: "1rem" }}
          variant="filled"
        >
          {translate("app.create.add_key_issues")}
        </UIButton>
      ) : (
        <>
          {dimensions.map((dimension) => (
            <DimensionItem
              description={dimension.description}
              id={dimension.id}
              image_url={dimension.image_url ?? localImage ?? null}
              is_custom_dimension={dimension.is_custom_dimension}
              isOnMap
              key={dimension.id}
              name={dimension.name}
              onEditSelect={onEditKeyIssueSelect}
              parent_topic_id={dimension.parent_topic_id}
              shouldHighlightSearchTerm={false}
            />
          ))}
        </>
      )}
    </StyledContainer>
  );
};
